import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import moment from "moment-timezone";
import {
  operatingHoursAPI,
  blockedTimesAPI,
  staffAPI,
} from "../../services/api";
import StaffLayout from "./StaffLayout";
import CreateAccount from "./CreateAccount";
import AutoClockOutSettings from "./AutoClockOutSettings";
import "react-datepicker/dist/react-datepicker.css";
import "./StudioSettingsPage.css";

const StudioSettingsPage = () => {
  const [activeTab, setActiveTab] = useState("operatingHours");
  const [operatingHours, setOperatingHours] = useState([]);
  const [blockedTimes, setBlockedTimes] = useState([]);
  const [notificationCategories, setNotificationCategories] = useState([]);
  const [notificationSubscriptions, setNotificationSubscriptions] = useState(
    []
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Operating Hours form state
  const [editingDay, setEditingDay] = useState(null);
  const [dayOfWeek, setDayOfWeek] = useState(1); // Monday
  const [openTime, setOpenTime] = useState("10:00");
  const [closeTime, setCloseTime] = useState("19:00");
  const [isOpen, setIsOpen] = useState(true);

  // Blocked Time form state
  const [editingBlockedTime, setEditingBlockedTime] = useState(null);
  const [blockedTitle, setBlockedTitle] = useState("");
  const [blockedStartTime, setBlockedStartTime] = useState(new Date());
  const [blockedEndTime, setBlockedEndTime] = useState(
    new Date(new Date().getTime() + 60 * 60 * 1000)
  ); // 1 hour later
  const [blockedDescription, setBlockedDescription] = useState("");
  const [affectedSuites, setAffectedSuites] = useState([]);
  const [allSuites, setAllSuites] = useState(false);

  // Notification subscription form state
  const [editingNotificationSubscription, setEditingNotificationSubscription] =
    useState(null);
  const [notificationLabel, setNotificationLabel] = useState("");
  const [notificationEmail, setNotificationEmail] = useState("");
  const [selectedNotificationCategories, setSelectedNotificationCategories] =
    useState(["all"]);
  const [notificationIsActive, setNotificationIsActive] = useState(true);

  const dayNames = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  const suiteOptions = [
    { id: 1, name: "Massage #1" },
    { id: 2, name: "Sauna #2" },
    { id: 3, name: "Sauna #3" },
    { id: 4, name: "Sauna #4" },
    { id: 5, name: "Sauna #5" },
    { id: 6, name: "Sauna #6" },
    { id: 7, name: "Sauna #7" },
    { id: 8, name: "Sauna #8" },
    { id: 9, name: "Red Light #9" },
    { id: 10, name: "Red Light #10" },
  ];

  // Fetch operating hours and blocked times
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (activeTab === "operatingHours") {
          const response = await operatingHoursAPI.getAllOperatingHours();
          setOperatingHours(response.data.operatingHours || []);
        } else if (activeTab === "blockedTimes") {
          const today = new Date();
          const oneMonthLater = new Date();
          oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);

          const response = await blockedTimesAPI.getBlockedTimesForRange(
            today.toISOString(),
            oneMonthLater.toISOString()
          );
          setBlockedTimes(response.data.blockedTimes || []);
        } else if (activeTab === "notifications") {
          const response = await staffAPI.getNotificationSubscriptions();
          setNotificationSubscriptions(response.data.subscriptions || []);
          setNotificationCategories(response.data.categories || []);
        }
      } catch (err) {
        console.error(`Error fetching ${activeTab}:`, err);
        setError(`Failed to load ${activeTab}. Please try again.`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activeTab]);

  // Handle operating hours form submission
  const handleOperatingHoursSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      const data = {
        dayOfWeek,
        openTime,
        closeTime,
        isOpen,
      };

      await operatingHoursAPI.setOperatingHours(data);

      // Refresh the operating hours list
      const response = await operatingHoursAPI.getAllOperatingHours();
      setOperatingHours(response.data.operatingHours || []);

      setSuccess("Operating hours updated successfully!");
      resetOperatingHoursForm();
    } catch (err) {
      console.error("Error updating operating hours:", err);
      setError("Failed to update operating hours. Please try again.");
    }
  };

  // Handle blocked time form submission
  const handleBlockedTimeSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      const data = {
        title: blockedTitle,
        startTime: blockedStartTime,
        endTime: blockedEndTime,
        description: blockedDescription,
        affectedSuites: allSuites ? [] : affectedSuites,
      };

      if (editingBlockedTime) {
        await blockedTimesAPI.updateBlockedTime(editingBlockedTime, data);
      } else {
        await blockedTimesAPI.createBlockedTime(data);
      }

      // Refresh the blocked times list
      const today = new Date();
      const oneMonthLater = new Date();
      oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);

      const response = await blockedTimesAPI.getBlockedTimesForRange(
        today.toISOString(),
        oneMonthLater.toISOString()
      );
      setBlockedTimes(response.data.blockedTimes || []);

      setSuccess("Blocked time saved successfully!");
      resetBlockedTimeForm();
    } catch (err) {
      console.error("Error saving blocked time:", err);
      setError("Failed to save blocked time. Please try again.");
    }
  };

  // Delete operating hours
  const handleDeleteOperatingHours = async (dayOfWeek) => {
    if (
      window.confirm(
        `Are you sure you want to delete operating hours for ${dayNames[dayOfWeek]}?`
      )
    ) {
      try {
        await operatingHoursAPI.deleteOperatingHours(dayOfWeek);

        // Refresh the operating hours list
        const response = await operatingHoursAPI.getAllOperatingHours();
        setOperatingHours(response.data.operatingHours || []);

        setSuccess("Operating hours deleted successfully!");
      } catch (err) {
        console.error("Error deleting operating hours:", err);
        setError("Failed to delete operating hours. Please try again.");
      }
    }
  };

  // Delete blocked time
  const handleDeleteBlockedTime = async (id) => {
    if (window.confirm("Are you sure you want to delete this blocked time?")) {
      try {
        await blockedTimesAPI.deleteBlockedTime(id);

        // Refresh the blocked times list
        const today = new Date();
        const oneMonthLater = new Date();
        oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);

        const response = await blockedTimesAPI.getBlockedTimesForRange(
          today.toISOString(),
          oneMonthLater.toISOString()
        );
        setBlockedTimes(response.data.blockedTimes || []);

        setSuccess("Blocked time deleted successfully!");
      } catch (err) {
        console.error("Error deleting blocked time:", err);
        setError("Failed to delete blocked time. Please try again.");
      }
    }
  };

  // Edit operating hours
  const handleEditOperatingHours = (hours) => {
    setEditingDay(hours.dayOfWeek);
    setDayOfWeek(hours.dayOfWeek);
    setOpenTime(hours.openTime);
    setCloseTime(hours.closeTime);
    setIsOpen(hours.isOpen);
  };

  // Edit blocked time
  const handleEditBlockedTime = (blockedTime) => {
    setEditingBlockedTime(blockedTime._id);
    setBlockedTitle(blockedTime.title);
    setBlockedStartTime(new Date(blockedTime.startTime));
    setBlockedEndTime(new Date(blockedTime.endTime));
    setBlockedDescription(blockedTime.description || "");
    setAffectedSuites(blockedTime.affectedSuites || []);
    setAllSuites(blockedTime.affectedSuites.length === 0);
  };

  // Reset operating hours form
  const resetOperatingHoursForm = () => {
    setEditingDay(null);
    setDayOfWeek(1);
    setOpenTime("10:00");
    setCloseTime("19:00");
    setIsOpen(true);
  };

  // Reset blocked time form
  const resetBlockedTimeForm = () => {
    setEditingBlockedTime(null);
    setBlockedTitle("");
    setBlockedStartTime(new Date());
    setBlockedEndTime(new Date(new Date().getTime() + 60 * 60 * 1000));
    setBlockedDescription("");
    setAffectedSuites([]);
    setAllSuites(false);
  };

  const resetNotificationForm = () => {
    setEditingNotificationSubscription(null);
    setNotificationLabel("");
    setNotificationEmail("");
    setSelectedNotificationCategories(["all"]);
    setNotificationIsActive(true);
  };

  // Handle suite selection
  const handleSuiteSelection = (suiteId) => {
    if (affectedSuites.includes(suiteId)) {
      setAffectedSuites(affectedSuites.filter((id) => id !== suiteId));
    } else {
      setAffectedSuites([...affectedSuites, suiteId]);
    }
  };

  const handleNotificationCategoryToggle = (categoryValue) => {
    setSelectedNotificationCategories((currentCategories) => {
      if (categoryValue === "all") {
        return ["all"];
      }

      const nextCategories = currentCategories.filter(
        (category) => category !== "all"
      );

      if (nextCategories.includes(categoryValue)) {
        const filteredCategories = nextCategories.filter(
          (category) => category !== categoryValue
        );

        return filteredCategories.length > 0 ? filteredCategories : ["all"];
      }

      return [...nextCategories, categoryValue];
    });
  };

  const handleNotificationSubscriptionSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      const payload = {
        label: notificationLabel,
        email: notificationEmail,
        categories: selectedNotificationCategories,
        isActive: notificationIsActive,
      };

      if (editingNotificationSubscription) {
        await staffAPI.updateNotificationSubscription(
          editingNotificationSubscription,
          payload
        );
      } else {
        await staffAPI.createNotificationSubscription(payload);
      }

      const response = await staffAPI.getNotificationSubscriptions();
      setNotificationSubscriptions(response.data.subscriptions || []);
      setNotificationCategories(response.data.categories || []);
      setSuccess("Notification subscription saved successfully!");
      resetNotificationForm();
    } catch (err) {
      console.error("Error saving notification subscription:", err);
      setError(
        err?.response?.data?.message ||
          "Failed to save notification subscription. Please try again."
      );
    }
  };

  const handleEditNotificationSubscription = (subscription) => {
    setEditingNotificationSubscription(subscription._id);
    setNotificationLabel(subscription.label || "");
    setNotificationEmail(subscription.email || "");
    setSelectedNotificationCategories(
      subscription.categories?.length > 0 ? subscription.categories : ["all"]
    );
    setNotificationIsActive(subscription.isActive !== false);
    setError("");
    setSuccess("");
  };

  const handleDeleteNotificationSubscription = async (subscriptionId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this staff notification subscription?"
      )
    ) {
      return;
    }

    try {
      await staffAPI.deleteNotificationSubscription(subscriptionId);
      const response = await staffAPI.getNotificationSubscriptions();
      setNotificationSubscriptions(response.data.subscriptions || []);
      setNotificationCategories(response.data.categories || []);
      setSuccess("Notification subscription deleted successfully!");

      if (editingNotificationSubscription === subscriptionId) {
        resetNotificationForm();
      }
    } catch (err) {
      console.error("Error deleting notification subscription:", err);
      setError(
        err?.response?.data?.message ||
          "Failed to delete notification subscription. Please try again."
      );
    }
  };

  const notificationCategoryLabelMap = notificationCategories.reduce(
    (map, category) => {
      map[category.value] = category.label;
      return map;
    },
    {}
  );

  return (
    <StaffLayout>
      <div className="studio-settings-page">
        <h1>Studio Settings</h1>

        {/* Tabs */}
        <div className="settings-tabs">
          <button
            className={activeTab === "operatingHours" ? "active" : ""}
            onClick={() => setActiveTab("operatingHours")}
          >
            Operating Hours
          </button>
          <button
            className={activeTab === "blockedTimes" ? "active" : ""}
            onClick={() => setActiveTab("blockedTimes")}
          >
            Blocked Times
          </button>
          <button
            className={activeTab === "notifications" ? "active" : ""}
            onClick={() => setActiveTab("notifications")}
          >
            Staff Notifications
          </button>
          <button
            className={activeTab === "accounts" ? "active" : ""}
            onClick={() => setActiveTab("accounts")}
          >
            Accounts
          </button>
          <button
            className={activeTab === "autoClockOut" ? "active" : ""}
            onClick={() => setActiveTab("autoClockOut")}
            data-testid="settings-tab-auto-clock-out"
          >
            Auto-Clock-Out
          </button>
        </div>

        {/* Notification area */}
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {/* Operating Hours Tab */}
        {activeTab === "operatingHours" && (
          <div className="operating-hours-section">
            <h2>Set Studio Operating Hours</h2>

            {/* Operating Hours Form */}
            <form
              onSubmit={handleOperatingHoursSubmit}
              className="settings-form"
            >
              <div className="form-group">
                <label htmlFor="dayOfWeek">Day of Week:</label>
                <select
                  id="dayOfWeek"
                  value={dayOfWeek}
                  onChange={(e) => setDayOfWeek(Number(e.target.value))}
                  required
                >
                  {dayNames.map((day, index) => (
                    <option key={index} value={index}>
                      {day}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={isOpen}
                    onChange={(e) => setIsOpen(e.target.checked)}
                  />
                  Studio is open on this day
                </label>
              </div>

              {isOpen && (
                <>
                  <div className="form-group">
                    <label htmlFor="openTime">Opening Time:</label>
                    <input
                      type="time"
                      id="openTime"
                      value={openTime}
                      onChange={(e) => setOpenTime(e.target.value)}
                      required={isOpen}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="closeTime">Closing Time:</label>
                    <input
                      type="time"
                      id="closeTime"
                      value={closeTime}
                      onChange={(e) => setCloseTime(e.target.value)}
                      required={isOpen}
                    />
                  </div>
                </>
              )}

              <div className="form-actions">
                <button type="submit" className="primary-btn">
                  {editingDay !== null ? "Update Hours" : "Save Hours"}
                </button>
                {editingDay !== null && (
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={resetOperatingHoursForm}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>

            {/* Operating Hours List */}
            <div className="hours-list">
              <h3>Current Operating Hours</h3>
              {loading ? (
                <p>Loading operating hours...</p>
              ) : operatingHours.length === 0 ? (
                <p>No operating hours set. Please add hours for each day.</p>
              ) : (
                <table className="settings-table">
                  <thead>
                    <tr>
                      <th>Day</th>
                      <th>Status</th>
                      <th>Hours</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {operatingHours
                      .sort((a, b) => a.dayOfWeek - b.dayOfWeek)
                      .map((hours) => (
                        <tr key={hours.dayOfWeek}>
                          <td>{dayNames[hours.dayOfWeek]}</td>
                          <td>{hours.isOpen ? "Open" : "Closed"}</td>
                          <td>
                            {hours.isOpen
                              ? `${moment(hours.openTime, "HH:mm").format(
                                  "h:mm A"
                                )} - ${moment(hours.closeTime, "HH:mm").format(
                                  "h:mm A"
                                )}`
                              : "N/A"}
                          </td>

                          <td>
                            <button
                              className="edit-btn"
                              onClick={() => handleEditOperatingHours(hours)}
                            >
                              Edit
                            </button>
                            <button
                              className="delete-btn"
                              onClick={() =>
                                handleDeleteOperatingHours(hours.dayOfWeek)
                              }
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {activeTab === "accounts" && (
          <div className="accounts-section">
            <div className="settings-section-intro">
              <h2>Create Staff-Managed Accounts</h2>
              <p>
                Create member, staff, massage therapist, and Luminary accounts
                from one place. New accounts receive a secure email to set
                their password and sign in.
              </p>
            </div>
            <div className="account-management-card">
              <CreateAccount
                title="Create Account"
                successTitle="Account Created"
                showCancelButton={false}
                onSuccess={(account) => {
                  setError("");
                  setSuccess(
                    `Created ${account.firstName} ${account.lastName} as a ${
                      account.role === "massage_therapist"
                        ? "massage therapist"
                        : account.role === "luminary"
                        ? "Luminary"
                        : account.role
                    }.`
                  );
                }}
              />
            </div>
          </div>
        )}

        {/* Blocked Times Tab */}
        {activeTab === "blockedTimes" && (
          <div className="blocked-times-section">
            <h2>Manage Blocked Times</h2>

            {/* Blocked Time Form */}
            <form onSubmit={handleBlockedTimeSubmit} className="settings-form">
              <div className="form-group">
                <label htmlFor="blockedTitle">Title:</label>
                <input
                  type="text"
                  id="blockedTitle"
                  value={blockedTitle}
                  onChange={(e) => setBlockedTitle(e.target.value)}
                  placeholder="e.g., Staff Meeting, Maintenance"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="blockedStartTime">Start Time:</label>
                <DatePicker
                  id="blockedStartTime"
                  selected={blockedStartTime}
                  onChange={(date) => setBlockedStartTime(date)}
                  showTimeSelect
                  dateFormat="MMMM d, yyyy h:mm aa"
                  minDate={new Date()}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="blockedEndTime">End Time:</label>
                <DatePicker
                  id="blockedEndTime"
                  selected={blockedEndTime}
                  onChange={(date) => setBlockedEndTime(date)}
                  showTimeSelect
                  dateFormat="MMMM d, yyyy h:mm aa"
                  minDate={blockedStartTime}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="blockedDescription">
                  Description (optional):
                </label>
                <textarea
                  id="blockedDescription"
                  value={blockedDescription}
                  onChange={(e) => setBlockedDescription(e.target.value)}
                  placeholder="Add details about why this time is blocked"
                  rows={3}
                />
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={allSuites}
                    onChange={(e) => {
                      setAllSuites(e.target.checked);
                      if (e.target.checked) {
                        setAffectedSuites([]);
                      }
                    }}
                  />
                  Block all suites
                </label>
              </div>

              {!allSuites && (
                <div className="form-group">
                  <label>Select affected suites:</label>
                  <div className="suite-selection">
                    {suiteOptions.map((suite) => (
                      <label key={suite.id} className="suite-checkbox">
                        <input
                          type="checkbox"
                          checked={affectedSuites.includes(suite.id)}
                          onChange={() => handleSuiteSelection(suite.id)}
                        />
                        {suite.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-actions">
                <button type="submit" className="primary-btn">
                  {editingBlockedTime
                    ? "Update Blocked Time"
                    : "Add Blocked Time"}
                </button>
                {editingBlockedTime && (
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={resetBlockedTimeForm}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>

            {/* Blocked Times List */}
            <div className="blocked-times-list">
              <h3>Upcoming Blocked Times</h3>
              {loading ? (
                <p>Loading blocked times...</p>
              ) : blockedTimes.length === 0 ? (
                <p>No blocked times scheduled.</p>
              ) : (
                <table className="settings-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Start Time</th>
                      <th>End Time</th>
                      <th>Affected Suites</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blockedTimes
                      .sort(
                        (a, b) => new Date(a.startTime) - new Date(b.startTime)
                      )
                      .map((blockedTime) => (
                        <tr key={blockedTime._id}>
                          <td>{blockedTime.title}</td>
                          <td>
                            {moment(blockedTime.startTime).format(
                              "MMM D, YYYY h:mm A"
                            )}
                          </td>
                          <td>
                            {moment(blockedTime.endTime).format(
                              "MMM D, YYYY h:mm A"
                            )}
                          </td>
                          <td>
                            {blockedTime.affectedSuites.length === 0
                              ? "All Suites"
                              : blockedTime.affectedSuites
                                  .map(
                                    (id) =>
                                      suiteOptions.find((s) => s.id === id)
                                        ?.name || `Suite ${id}`
                                  )
                                  .join(", ")}
                          </td>
                          <td>
                            <button
                              className="edit-btn"
                              onClick={() => handleEditBlockedTime(blockedTime)}
                            >
                              Edit
                            </button>
                            <button
                              className="delete-btn"
                              onClick={() =>
                                handleDeleteBlockedTime(blockedTime._id)
                              }
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {activeTab === "notifications" && (
          <div className="notifications-section">
            <h2>Staff Email Notification Subscriptions</h2>
            <p className="settings-intro">
              Add email addresses for staff members and choose which operational
              notification categories they should receive.
            </p>

            <form
              onSubmit={handleNotificationSubscriptionSubmit}
              className="settings-form"
            >
              <div className="form-group">
                <label htmlFor="notificationLabel">Label (optional):</label>
                <input
                  type="text"
                  id="notificationLabel"
                  value={notificationLabel}
                  onChange={(e) => setNotificationLabel(e.target.value)}
                  placeholder="e.g., Front Desk, Lakisha"
                />
              </div>

              <div className="form-group">
                <label htmlFor="notificationEmail">Email address:</label>
                <input
                  type="text"
                  id="notificationEmail"
                  value={notificationEmail}
                  onChange={(e) => setNotificationEmail(e.target.value)}
                  placeholder="staff@divitiadora.com"
                  required
                />
              </div>

              <div className="form-group">
                <label>Notification categories:</label>
                <div className="notification-category-grid">
                  {notificationCategories.map((category) => (
                    <label
                      key={category.value}
                      className="notification-category-option"
                    >
                      <input
                        type="checkbox"
                        checked={selectedNotificationCategories.includes(
                          category.value
                        )}
                        onChange={() =>
                          handleNotificationCategoryToggle(category.value)
                        }
                      />
                      <span className="notification-category-copy">
                        <span className="notification-category-label">
                          {category.label}
                        </span>
                        <span className="notification-category-description">
                          {category.description}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={notificationIsActive}
                    onChange={(e) => setNotificationIsActive(e.target.checked)}
                  />
                  Subscription is active
                </label>
              </div>

              <div className="form-actions">
                <button type="submit" className="primary-btn">
                  {editingNotificationSubscription
                    ? "Update Subscription"
                    : "Add Subscription"}
                </button>
                {editingNotificationSubscription && (
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={resetNotificationForm}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>

            <div className="notification-subscription-list">
              <h3>Current Notification Recipients</h3>
              {loading ? (
                <p>Loading notification subscriptions...</p>
              ) : notificationSubscriptions.length === 0 ? (
                <p>
                  No staff notification subscriptions have been added yet. When
                  empty, the app falls back to the env-based notification
                  recipients.
                </p>
              ) : (
                <table className="settings-table">
                  <thead>
                    <tr>
                      <th>Label</th>
                      <th>Email</th>
                      <th>Categories</th>
                      <th>Status</th>
                      <th>Updated</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notificationSubscriptions.map((subscription) => (
                      <tr key={subscription._id}>
                        <td>{subscription.label || "Untitled"}</td>
                        <td>{subscription.email}</td>
                        <td>
                          <div className="notification-badge-list">
                            {(subscription.categories || ["all"]).map(
                              (category) => (
                                <span
                                  key={`${subscription._id}-${category}`}
                                  className="notification-badge"
                                >
                                  {notificationCategoryLabelMap[category] ||
                                    category}
                                </span>
                              )
                            )}
                          </div>
                        </td>
                        <td>
                          {subscription.isActive ? "Active" : "Inactive"}
                        </td>
                        <td>
                          {subscription.updatedAt
                            ? moment(subscription.updatedAt).format(
                                "MMM D, YYYY h:mm A"
                              )
                            : "N/A"}
                        </td>
                        <td>
                          <button
                            className="edit-btn"
                            onClick={() =>
                              handleEditNotificationSubscription(subscription)
                            }
                          >
                            Edit
                          </button>
                          <button
                            className="delete-btn"
                            onClick={() =>
                              handleDeleteNotificationSubscription(
                                subscription._id
                              )
                            }
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* TIME-007 §3 — Auto-Clock-Out admin config tab */}
        {activeTab === "autoClockOut" && <AutoClockOutSettings />}
      </div>
    </StaffLayout>
  );
};

export default StudioSettingsPage;
